/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * File: useSalesOrderTypeService.ts
 * Project: papa-bear
 * -----
 * Last Modified: 30 Nov 2021 5:43:12 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { encodeValue } from 'common/helpers/encodeValue';
import lodash from 'lodash';
import { DeliveryTypeModel } from 'models/DeliveryTypeModel';
import { SalesOrderTypeModel } from 'models/SalesOrderTypeModel';
import { ReduxState } from 'redux/rootReducer';

/* ****************** Interfaces ****************** */
export interface GetSalesOrderTypesParams {
  dataAreaId?: string;
  salesOrderTypeContains?: string;
  offset: number;
}

export interface GetSalesOrderTypesResponseModel {
  data: SalesOrderTypeModel[];
  totalCount: number;
}

export interface GetDeliveryTypesParams {
  dataAreaId?: string;
  name?: string;
  offset: number;
}
export interface GetDeliveryTypesResponseModel {
  data: DeliveryTypeModel[];
  totalCount: number;
}

/* ****************** Variables ****************** */
const grannyFannyNestlerodeBaseUrl = process.env.REACT_APP_GRANNY_FANNY_NESTLERODE_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_GRANNY_FANNY_NESTLERODE;

const baseQuery = fetchBaseQuery({
  baseUrl: grannyFannyNestlerodeBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

export const salesOrderTypesApi = createApi({
  reducerPath: 'salesOrderTypesApi',
  baseQuery,
  endpoints: (builder) => ({
    getSalesOrderTypes: builder.query<GetSalesOrderTypesResponseModel, GetSalesOrderTypesParams>({
      query: ({ dataAreaId, offset, salesOrderTypeContains }) => ({
        url: `salesOrderTypes`,
        params: { dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '', offset, salesOrderTypeContains }
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().salesOrderTypesApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    getSalesOrderTypeContains: builder.query<GetSalesOrderTypesResponseModel, GetSalesOrderTypesParams>({
      query: ({ dataAreaId, salesOrderTypeContains, offset }) => ({
        url: `salesOrderTypes`,
        params: { dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '', salesOrderTypeContains, offset }
      })
    }),
    getDeliveryTypes: builder.query<GetDeliveryTypesResponseModel, void>({
      query: () => ({
        url: `deliveryTypes`,
        params: {}
      })
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetSalesOrderTypesQuery, useGetSalesOrderTypeContainsQuery, useLazyGetSalesOrderTypeContainsQuery, useGetDeliveryTypesQuery, useGetVersionQuery, useGetApiNameQuery } =
  salesOrderTypesApi;
